<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card elevation="2" class="pa-4 rounded-xl">
          <div class="d-flex align-center justify-space-between">
            <div>
              <h2 class="text-h4 font-weight-bold primary--text mb-1">
                Panel de Presupuestos
              </h2>
              <p class="text-subtitle-1 grey--text text--darken-1 mb-0">
                Gestión, distribución y rendimiento del presupuesto de Marketing
              </p>
            </div>
            
            <!-- Selector de Instancia Global e Histórico -->
            <div class="d-flex align-center">
              <!-- Badge Solo Lectura -->
              <v-chip v-if="!isDirector" color="amber darken-2" dark small label class="mr-3">
                <v-icon left small>mdi-eye-outline</v-icon> Solo Lectura
              </v-chip>
              <v-chip v-else color="success" dark small label class="mr-3">
                <v-icon left small>mdi-shield-check</v-icon> Director
              </v-chip>
              
              <v-card class="pa-2 rounded-lg d-flex align-center" elevation="0" color="transparent">
                <!-- Botón Nuevo Ciclo (Solo Director) -->
                <v-btn
                  v-if="isDirector"
                  color="green darken-1"
                  dark
                  small
                  class="mr-4 rounded-lg font-weight-bold"
                  elevation="2"
                  @click="iniciarNuevoCiclo"
                >
                  <v-icon left small>mdi-plus-circle</v-icon>
                  Nuevo
                </v-btn>

                <!-- Selector de Ciclos (Histórico) -->
                <v-select
                  v-model="cicloSeleccionadoId"
                  :items="opcionesCiclos"
                  label="Ciclo"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-history"
                  color="primary"
                  class="mr-3"
                  style="min-width: 280px;"
                  @change="onCicloChange"
                  :loading="cargandoCiclos"
                  :disabled="cargandoCiclos || opcionesCiclos.length === 0"
                ></v-select>

                <!-- Selector de Instancia -->
                <v-select
                  v-model="instanciaSeleccionada"
                  :items="instancias"
                  label="Instancia Activa"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-domain"
                  color="primary"
                  style="min-width: 180px;"
                  @change="onInstanciaChange"
                  :disabled="!isDirector"
                ></v-select>
              </v-card>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content with Tabs -->
    <v-card elevation="2" class="rounded-xl overflow-hidden">
      <v-tabs
        v-model="tab"
        background-color="transparent"
        color="primary"
        slider-size="3"
        show-arrows
        class="px-4 pt-2 border-bottom"
      >
        <v-tab v-for="item in tabs" :key="item.value" class="font-weight-medium text-capitalize">
          <v-icon left size="20">{{ item.icon }}</v-icon>
          {{ item.text }}
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-items v-model="tab" class="pa-4 bg-transparent">
        <v-tab-item v-for="item in tabs" :key="item.value">
          <v-slide-y-transition mode="out-in">
            <!-- Dynamic Components will be rendered here -->
            <component 
              :is="item.component" 
              :instancia="instanciaSeleccionada"
              :ciclo-id="cicloSeleccionadoId"
              :is-activo="!cicloSeleccionadoId || (ciclosHistorico.length > 0 && cicloSeleccionadoId === ciclosHistorico[0].id) || cicloCreacion"
              :is-director="isDirector"
              :ciclo-creacion="cicloCreacion"
              :has-clonar="ciclosHistorico.length > 0"
              v-if="tab === tabs.indexOf(item) && (cicloSeleccionadoId || item.value === 'config')"
              @nuevo-ciclo-guardado="onNuevoCicloGuardado"
            ></component>
            
            <div v-else-if="tab === tabs.indexOf(item) && !cicloSeleccionadoId && !cargandoCiclos && item.value !== 'config'" class="text-center py-12">
              <v-icon size="64" color="grey lighten-2">mdi-alert-circle-outline</v-icon>
              <h3 class="text-h6 grey--text mt-4">No hay ciclos disponibles</h3>
              <p class="grey--text">Dirígete a la pestaña "Configuración Inicial" para crear el primer ciclo de presupuestos.</p>
            </div>
          </v-slide-y-transition>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';

// Componentes hijos (Tabs) importados dinámicamente para aligerar la carga principal
const TabConfiguracion    = () => import('./tabs/TabConfiguracion.vue');
const TabDistribucion     = () => import('./tabs/TabDistribucion.vue');
const TabRendimiento      = () => import('./tabs/TabRendimiento.vue');
const TabRendimientoDiario = () => import('./tabs/TabRendimientoDiario.vue');
const TabConsolidado      = () => import('./tabs/TabConsolidado.vue');
const TabProyectosOffline = () => import('./tabs/TabProyectosOffline.vue');
const TabDetalleMetaGoogle = () => import('./tabs/TabDetalleMetaGoogle.vue');

export default {
  name: 'PanelPresupuestos',
  
  components: {
    TabConfiguracion,
    TabDistribucion,
    TabRendimiento,
    TabRendimientoDiario,
    TabConsolidado,
    TabProyectosOffline,
    TabDetalleMetaGoogle
  },

  data() {
    return {
      tab: 0,
      instanciaSeleccionada: 'INBI', // Valor por defecto
      instancias: ['INBI', 'FAST'],
      ciclosHistorico: [],
      cicloSeleccionadoId: null,
      cargandoCiclos: false,
      cicloCreacion: false,
      tabs: [
        { 
          text: 'Dashboard Consolidado', 
          value: 'dashboard', 
          icon: 'mdi-view-dashboard-outline',
          component: 'TabConsolidado'
        },
        { 
          text: 'Configuración Inicial', 
          value: 'config', 
          icon: 'mdi-cog-outline',
          component: 'TabConfiguracion'
        },
        { 
          text: 'Distribución', 
          value: 'distribucion', 
          icon: 'mdi-chart-pie',
          component: 'TabDistribucion'
        },
        {
          text: 'Rendimiento (APIs)',
          value: 'rendimiento',
          icon: 'mdi-google-ads',
          component: 'TabRendimiento'
        },
        {
          text: 'Rendimiento Diario',
          value: 'diario',
          icon: 'mdi-calendar-check',
          component: 'TabRendimientoDiario'
        },
        {
          text: 'Detalle Meta y Google',
          value: 'detalle-api',
          icon: 'mdi-table-large',
          component: 'TabDetalleMetaGoogle'
        },
        {
          text: 'Proyectos Offline (Capex)',
          value: 'offline',
          icon: 'mdi-storefront',
          component: 'TabProyectosOffline'
        }
      ]
    };
  },

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),
    
    // Puestos con permisos de escritura total en el Panel de Presupuestos
    isDirector() {
      const usuario = this.getdatosUsuarioERPERP;
      if (!usuario || !usuario.idpuesto) return false;
      const PUESTOS_DIRECTIVOS = [6, 20, 32, 43, 48, 54, 3, 7, 12, 11];
      return PUESTOS_DIRECTIVOS.includes(usuario.idpuesto);
    },

    opcionesCiclos() {
      return this.ciclosHistorico.map((c, index) => {
        const inicio = moment(c.fecha_inicio).format('DD MMM YYYY');
        const fin = moment(c.fecha_fin).format('DD MMM YYYY');
        const estatus = index === 0 ? ' (Activo)' : ' (Histórico)';
        return {
          text: `Ciclo: ${inicio} - ${fin}${estatus}`,
          value: c.id
        };
      });
    }
  },

  created() {
    this.fetchCiclos();
  },

  methods: {
    async fetchCiclos() {
      this.cargandoCiclos = true;
      try {
         const res = await this.$http.get(`api/presupuestos/ciclos/${this.instanciaSeleccionada}`);
         this.ciclosHistorico = res.data || [];
         if(this.ciclosHistorico.length > 0) {
            this.cicloSeleccionadoId = this.ciclosHistorico[0].id; // El más reciente (ID desc) es el activo
            this.cicloCreacion = false;
         } else {
            this.cicloSeleccionadoId = null;
            this.cicloCreacion = true;
         }
      } catch (err) {
         console.error("Error obteniendo ciclos:", err);
      } finally {
         this.cargandoCiclos = false;
      }
    },

    iniciarNuevoCiclo() {
      this.cicloCreacion = true;
      this.cicloSeleccionadoId = null;
      this.tab = 0; // Llevar a TabConfiguracion
    },

    onNuevoCicloGuardado() {
      // Cuando se guarda un ciclo nuevo, refrescamos la lista
      this.fetchCiclos();
    },

    onInstanciaChange(newInstancia) {
      console.log('Instancia cambiada a:', newInstancia);
      this.fetchCiclos();
    },

    onCicloChange(newCicloId) {
      console.log('Ciclo histórico seleccionado:', newCicloId);
      this.cicloCreacion = false; // Al elegir un ciclo histórico, salimos del modo creación
    }
  }
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.bg-transparent {
  background-color: transparent !important;
}
/* Suavizar transiciones */
.v-tab {
  transition: all 0.3s ease;
}
.v-tab--active {
  font-weight: 700;
  transform: translateY(-2px);
}
</style>
